import type { LocatedOsmElement } from "../osm.ts";
import { fetchCategoryOsmElements } from "../postpass.ts";
import type { PoiCategory } from "./categories.ts";
import {
  createPoiSearchArea,
  isPoiSearchStale,
  type PoiSearchArea,
  type PoiSearchCamera,
} from "./area.ts";

const POI_SEARCH_TIMEOUT_MS = 30_000;
const POI_SEARCH_LIMIT = 200;

export type PoiSearchState =
  | { status: "idle" }
  | {
    status: "loading";
    category: PoiCategory;
    area: PoiSearchArea;
    source: "search" | "area";
  }
  | {
    status: "success";
    category: PoiCategory;
    area: PoiSearchArea;
    results: LocatedOsmElement[];
    truncated: boolean;
    // `stale` means the camera has moved enough that these results may no
    // longer represent the area the user is looking at.
    stale: boolean;
  }
  | {
    status: "empty";
    category: PoiCategory;
    area: PoiSearchArea;
    stale: boolean;
  }
  | {
    status: "error";
    category: PoiCategory;
    area: PoiSearchArea;
    stale: boolean;
    message: string;
  };

export interface PoiSearchController {
  // Starts a fresh category search from the current camera.
  search: (category: PoiCategory, camera: PoiSearchCamera) => void;
  // Reruns the active category search, typically for "search in this area".
  refresh: (camera?: PoiSearchCamera) => void;
  // Updates stale status from camera movement without rerunning the query.
  updateCamera: (camera: PoiSearchCamera) => void;
  // Cancels in-flight work and returns the controller to the idle state.
  reset: () => void;
  cancel: () => void;
}

type FetchCategoryOsmElements = typeof fetchCategoryOsmElements;

interface PoiSearchControllerOptions {
  // These overrides exist mainly to keep focused tests small and deterministic.
  fetchCategoryElements?: FetchCategoryOsmElements;
  resultLimit?: number;
  timeoutMs?: number;
}

export function createPoiSearchController(
  onState: (state: PoiSearchState) => void,
  options: PoiSearchControllerOptions = {},
): PoiSearchController {
  const fetchCategoryElements = options.fetchCategoryElements ??
    fetchCategoryOsmElements;
  const resultLimit = positiveInteger(
    options.resultLimit ?? POI_SEARCH_LIMIT,
    "Invalid POI search limit",
  );
  const timeoutMs = positiveInteger(
    options.timeoutMs ?? POI_SEARCH_TIMEOUT_MS,
    "Invalid POI search timeout",
  );
  let state: PoiSearchState = { status: "idle" };
  let currentCamera: PoiSearchCamera | undefined;
  let abortController: AbortController | undefined;
  let requestId = 0;

  function emit(next: PoiSearchState): void {
    state = next;
    onState(next);
  }

  function cancel(): void {
    requestId++;
    abortController?.abort();
    abortController = undefined;
  }

  function reset(): void {
    cancel();
    if (state.status !== "idle") {
      emit({ status: "idle" });
    }
  }

  function updateCamera(camera: PoiSearchCamera): void {
    currentCamera = camera;
    switch (state.status) {
      case "idle":
      case "loading":
        return;
      case "success":
      case "empty":
      case "error": {
        const stale = isPoiSearchStale(state.area, camera);
        if (stale !== state.stale) {
          emit({ ...state, stale });
        }
        return;
      }
    }
  }

  async function run(
    category: PoiCategory,
    camera: PoiSearchCamera,
    source: "search" | "area",
  ): Promise<void> {
    currentCamera = camera;
    cancel();
    const currentRequestId = requestId;
    const controller = new AbortController();
    abortController = controller;
    const area = createPoiSearchArea(camera);
    let timedOut = false;
    const timeoutId = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    emit({ status: "loading", category, area, source });

    try {
      const fetchedResults = await fetchCategoryElements(
        category,
        { center: area.center, bounds: area.bounds },
        resultLimit + 1,
        controller.signal,
      );
      if (controller.signal.aborted || currentRequestId !== requestId) {
        return;
      }

      const truncated = fetchedResults.length > resultLimit;
      const results = truncated
        ? fetchedResults.slice(0, resultLimit)
        : fetchedResults;
      const stale = currentCamera
        ? isPoiSearchStale(area, currentCamera)
        : false;
      emit(
        results.length === 0
          ? { status: "empty", category, area, stale }
          : { status: "success", category, area, results, truncated, stale },
      );
    } catch (err) {
      if (
        (controller.signal.aborted && !timedOut) ||
        currentRequestId !== requestId
      ) {
        return;
      }

      if (!timedOut) {
        console.error("POI search failed:", err);
      }
      const stale = currentCamera
        ? isPoiSearchStale(area, currentCamera)
        : false;
      emit({
        status: "error",
        category,
        area,
        stale,
        message: timedOut ? "POI search timed out." : "POI search failed.",
      });
    } finally {
      globalThis.clearTimeout(timeoutId);
      if (abortController === controller) {
        abortController = undefined;
      }
    }
  }

  return {
    search: (category, camera) => {
      void run(category, camera, "search");
    },
    refresh: (camera = currentCamera) => {
      if (state.status === "idle" || !camera) return;
      void run(state.category, camera, "area");
    },
    updateCamera,
    reset,
    cancel,
  };
}

function positiveInteger(value: number, label: string): number {
  if (Number.isSafeInteger(value) && value > 0) return value;
  throw new RangeError(`${label}: ${value}`);
}
